import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/components/ui/Icon';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { TextInput } from '@/components/ui/TextInput';
import { TextArea } from '@/components/ui/TextArea';
import { toast } from '@/components/ui/Toast';
import { agentApi, type ScreenPop, type CallTranscript } from '@/shared/api/agentApi';
import { useMaxWidth, BP } from '@/shared/hooks/useMediaQuery';
import { useSipStore } from '../stores/sipStore';
import { useCustomerCard } from '../stores/customerCard';
import { NotesChat } from './NotesChat';
import { SoftphonePanel } from './SoftphonePanel';

interface DispositionOption { code: string; label: string; color?: string }

/**
 * Unified floating customer card. Pops automatically the moment a call starts
 * (outbound dial or answered inbound), keyed on the active call's number.
 * Shows the CRM contact (or a blank, auto-saving form when unknown), the call
 * wrap-up (disposition + agent notes) INSIDE the card, and — once the recording
 * is transcribed — the auto transcript next to the AI summary. Everything saves
 * automatically (debounced); the agent never has to press "save".
 */
/**
 * `inline`: אותו כרטיס בדיוק, אבל מוטמע במסך במקום לרחף מעליו.
 *
 * במסך העבודה על קמפיין הכרטיס **הוא** התוכן — פופ-אפ שמכסה את ציר הזמן
 * ואת פאנל הפעולות מסתיר בדיוק את מה שהנציג צריך לצדו. הלוגיקה, הטעינות
 * והשמירה האוטומטית זהות; רק המעטפת משתנה.
 */
export function CustomerCard({ inline = false }: { inline?: boolean } = {}) {
  const number = useCustomerCard((s) => s.number);
  const open = useCustomerCard((s) => s.open);
  const close = useCustomerCard((s) => s.close);
  const suppressFloating = useCustomerCard((s) => s.suppressFloating);
  const callState = useSipStore((s) => s.state);
  // השיחה החיה מוצגת בתוך הכרטיס עצמו: כל עוד יש שיחה (או סיכום שלה) הנציג
  // רואה טיימר ופקדים — ולא נשאר מול מסך CRM בלי שום עדות שהוא בשיחה.
  const activeCall = useSipStore((s) => s.active);
  const showPhone = !!activeCall || callState === 'ringing-in' || callState === 'wrap-up';
  // מתחת לרוחב הזה שלוש עמודות נדחסות — עוברים לעמודה אחת, סופטפון קודם.
  const stacked = useMaxWidth(BP.stack);

  const [pop, setPop] = useState<ScreenPop | null>(null);
  const [loading, setLoading] = useState(false);
  const [contactId, setContactId] = useState<number | null>(null);

  // Editable card fields (auto-saved).
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');

  // Wrap-up (auto-saved).
  const [disposition, setDisposition] = useState('');
  const [agentNotes, setAgentNotes] = useState('');
  const [dispositions, setDispositions] = useState<DispositionOption[]>([]);

  // AI transcript + summary (polled).
  const [tr, setTr] = useState<CallTranscript | null>(null);

  // A past call's summary the agent opened from the history list.
  const [pastCall, setPastCall] = useState<CallTranscript | null>(null);
  const [pastLoading, setPastLoading] = useState<number | null>(null);

  async function openPast(callId: number) {
    setPastLoading(callId);
    try {
      setPastCall(await agentApi.callTranscript(callId));
    } catch {
      setPastCall(null);
    } finally {
      setPastLoading(null);
    }
  }

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load CRM context + dispositions when the card opens for a number.
  useEffect(() => {
    if (!open || !number) return;
    setLoading(true);
    setPop(null); setContactId(null); setTr(null);
    setName(''); setEmail(''); setCompany(''); setDisposition(''); setAgentNotes('');

    void agentApi.screenPop(number).then((p) => {
      setPop(p);
      if (p?.contact) {
        setContactId(p.contact.id);
        setName(p.contact.name ?? '');
        setEmail(p.contact.email ?? '');
        setCompany(p.contact.company ?? '');
      }
    }).catch(() => {}).finally(() => setLoading(false));

    void agentApi.callDispositions().then((d) => setDispositions(d as DispositionOption[])).catch(() => {});
  }, [open, number]);

  // Poll transcript + AI summary until it's done (recording → STT → Gemini runs
  // server-side after hangup). Only while the card is open with a number.
  useEffect(() => {
    if (!open || !number) return;
    let alive = true;
    const poll = () => {
      void agentApi.transcriptLatest(number).then((t) => {
        if (!alive) return;
        setTr(t);
        // Seed agent notes / disposition from the saved call if the agent hasn't typed.
        if (t?.agent_notes && !agentNotes) setAgentNotes(t.agent_notes);
        if (t?.disposition && !disposition) setDisposition(t.disposition);
      }).catch(() => {});
    };
    poll();
    const id = setInterval(poll, 6000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, number]);

  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsSaved, setDetailsSaved] = useState(false);

  // Explicit "save details" — for a NEW customer the agent fills the card and
  // presses save (no silent auto-create). For an existing contact, edits are
  // saved with the same button. Returns the (new) contact id.
  async function saveDetails() {
    if (!number) return;
    setSavingDetails(true);
    const fields = company ? { company } : undefined;
    try {
      if (contactId) {
        await agentApi.updateContact(contactId, { name, email, fields });
      } else {
        const c = await agentApi.createContact({ phone: number, name, email, fields });
        if (c && typeof (c as { id?: number }).id === 'number') setContactId((c as { id: number }).id);
      }
      setDetailsSaved(true);
      toast('הפרטים נשמרו', 'ok');
      setTimeout(() => setDetailsSaved(false), 2500);
    } catch {
      toast('שגיאה בשמירת הפרטים', 'danger');
    } finally {
      setSavingDetails(false);
    }
  }

  // Wrap-up (disposition + agent notes) still auto-saves, debounced, once a
  // disposition is picked — the call summary lives in the card and shouldn't
  // need a separate button. Picking a disposition also marks the call as
  // summarized so the blocking WrapupOverlay won't pop again at hangup.
  const markSummarized = useCustomerCard((s) => s.markSummarized);
  useEffect(() => {
    if (!open || !number || !disposition) return;
    markSummarized(number);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void agentApi.disposeLatestCall({ number, disposition, notes: agentNotes || undefined }).catch(() => {});
    }, 1200);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [open, number, disposition, agentNotes, markSummarized]);

  // העותק הצף נעלם כשמסך כלשהו מציג את הכרטיס בעצמו.
  if (!open || !number) return null;
  if (!inline && suppressFloating) return null;

  const known = !!pop?.contact;
  const callOver = callState === 'wrap-up' || callState === 'ready' || callState === 'idle';

  const body = (
      <div style={inline ? {
        width: '100%', background: 'var(--surface)', borderRadius: 'var(--r-4)',
        border: '1px solid var(--line)',
      } : {
        // עמודת הסופטפון מוסיפה 320px + מרווח — בלי הרחבה שלוש העמודות נדחסות.
        width: '100%', maxWidth: showPhone && !stacked ? 1220 : 880, maxHeight: '94vh', overflow: 'auto',
        background: 'var(--surface)', borderRadius: 'var(--r-4)',
        border: '1px solid var(--line)', boxShadow: 'var(--shadow-3)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 'var(--pad-card)', borderBottom: '1px solid var(--line)' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--accent-soft)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon name="user" size={22} color="var(--accent)" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--t-xl)', fontWeight: 700 }}>{name || (known ? 'לקוח' : 'לקוח חדש')}</div>
            <div dir="ltr" style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{number}</div>
          </div>
          {!known && <StatusBadge tone="warn">לא מזוהה — כרטיס חדש</StatusBadge>}
          <button type="button" className="btn ghost" onClick={close} aria-label="סגור" title="סגור" style={{ height: 32, width: 32, padding: 0 }}>
            <Icon name="close" size={16} />
          </button>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: stacked ? '1fr' : showPhone ? '1fr 1fr 320px' : '1fr 1fr',
          gap: 'var(--gap)', padding: 'var(--pad-card)',
        }}>
          {/* השיחה החיה. בפריסה מוערמת היא עולה לראש (order: -1) — פקדי השיחה
              חשובים יותר מפרטי ה-CRM כשאין מקום להציג את שניהם זה לצד זה. */}
          {showPhone && (
            <div className="stack" style={stacked ? { order: -1 } : { gridColumn: 3, gridRow: '1 / -1' }}>
              <div style={{ position: 'sticky', top: 0 }}>
                <SoftphonePanel bare hideFloatToggle />
                {callState === 'wrap-up' && <FinishWrapupButton number={number} />}
              </div>
            </div>
          )}

          {/* LEFT: customer details (editable, auto-saved) + wrap-up */}
          <div className="stack">
            <Section title={known ? 'פרטי לקוח' : 'כרטיס לקוח חדש'}>
              <Field label="שם"><TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="שם הלקוח" /></Field>
              <Field label="חברה"><TextInput value={company} onChange={(e) => setCompany(e.target.value)} placeholder="שם החברה" /></Field>
              <Field label="אימייל"><TextInput type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" /></Field>
              {pop?.contact?.tags && pop.contact.tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {pop.contact.tags.map((t) => (
                    <span key={t} style={{ fontSize: 'var(--t-xs)', background: 'var(--accent-soft)', color: 'var(--accent-ink)', borderRadius: 'var(--r-1)', padding: '1px 8px' }}>{t}</span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" className="btn primary" onClick={() => void saveDetails()} disabled={savingDetails}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Icon name={detailsSaved ? 'ok' : 'check'} size={14} />
                  {savingDetails ? 'שומר…' : detailsSaved ? 'נשמר' : known ? 'עדכן פרטים' : 'שמור פרטים'}
                </button>
              </div>
            </Section>

            <Section title="סיכום שיחה" hint="נשמר אוטומטית">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {dispositions.map((d) => (
                  <button key={d.code} type="button" className={`btn ${disposition === d.code ? 'primary' : ''}`}
                    style={{ fontSize: 'var(--t-sm)', padding: '4px 10px' }} onClick={() => setDisposition(d.code)}>
                    {d.label}
                  </button>
                ))}
              </div>
              <Field label="הערות הנציג">
                <TextArea value={agentNotes} onChange={(e) => setAgentNotes(e.target.value)} rows={4} placeholder="מה שהנציג רוצה לרשום על השיחה…" />
              </Field>
            </Section>

            <Section title="הערות מהירות" hint="כל שורה נשמרת מיד — כמו צ'אט">
              <NotesChat phone={number} maxHeight={200} />
            </Section>
          </div>

          {/* RIGHT: recent calls + auto transcript + AI summary */}
          <div className="stack">
            <Section title="סיכום AI אוטומטי" hint="מתמלול ההקלטה">
              {!callOver ? (
                <Muted>הסיכום יופק אוטומטית בסיום השיחה.</Muted>
              ) : !tr || tr.transcript_status === 'pending' || tr.transcript_status === 'processing' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
                  <span className="spin" style={{ width: 14, height: 14, border: '2px solid var(--line)', borderTopColor: 'var(--accent)', borderRadius: '50%' }} />
                  מתמלל ומסכם את ההקלטה…
                </div>
              ) : tr.summary ? (
                <div className="stack" style={{ gap: 8 }}>
                  {tr.sentiment && (
                    <StatusBadge tone={tr.sentiment === 'positive' ? 'ok' : tr.sentiment === 'negative' ? 'danger' : 'neutral'}>
                      {tr.sentiment === 'positive' ? 'חיובי' : tr.sentiment === 'negative' ? 'שלילי' : 'ניטרלי'}
                    </StatusBadge>
                  )}
                  <div style={{ fontSize: 'var(--t-sm)', lineHeight: 1.6 }}>{tr.summary}</div>
                  {tr.highlights.length > 0 && (
                    <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
                      {tr.highlights.map((h, i) => <li key={i}>{h}</li>)}
                    </ul>
                  )}
                </div>
              ) : (
                <Muted>לא הופק סיכום (ייתכן שאין הקלטה זמינה).</Muted>
              )}
            </Section>

            <Section title="תמלול השיחה" hint="אוטומטי">
              {tr && tr.transcript.length > 0 ? (
                <div className="stack" style={{ gap: 4, maxHeight: 220, overflow: 'auto' }}>
                  {tr.transcript.map((line, i) => (
                    <div key={i} style={{ fontSize: 'var(--t-sm)' }}>
                      <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>
                        {line.speaker === 'agent' ? 'נציג' : line.speaker === 'customer' ? 'לקוח' : (line.speaker ?? '')}{line.t ? ` · ${line.t}` : ''}:
                      </span>{' '}
                      {line.txt}
                    </div>
                  ))}
                </div>
              ) : (
                <Muted>{callOver ? 'התמלול יופיע כאן ברגע שיהיה מוכן.' : 'התמלול יופק בסיום השיחה.'}</Muted>
              )}
            </Section>

            {/* `recent_calls` עשוי לא לחזור כלל (לקוח חדש, או תשובה חלקית).
                בלי ההגנה הכרטיס כולו קורס על undefined.length. */}
            {(pop?.recent_calls?.length ?? 0) > 0 && (
              <Section title="סיכומי עבר" hint="לחץ לצפייה">
                {pop!.recent_calls!.slice(0, 6).map((rc) => (
                  <button key={rc.id} type="button" onClick={() => void openPast(rc.id)}
                    style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 8, fontSize: 'var(--t-xs)',
                      color: 'var(--text-2)', background: pastCall?.call_id === rc.id ? 'var(--accent-soft)' : 'var(--surface-2)',
                      border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: '6px 10px', cursor: 'pointer', textAlign: 'start' }}>
                    <span><Icon name={rc.direction === 'inbound' ? 'phoneIn' : 'phoneOut'} size={11} /> {rc.started_at ? new Date(rc.started_at).toLocaleDateString('he-IL') : ''}</span>
                    <span style={{ color: 'var(--muted)' }}>{pastLoading === rc.id ? 'טוען…' : (rc.disposition ?? rc.status)}</span>
                  </button>
                ))}
                {pastCall && (
                  <div style={{ marginTop: 6, padding: 10, background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
                    {pastCall.summary ? (
                      <>
                        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 4 }}>
                          סיכום {pastCall.started_at ? `· ${new Date(pastCall.started_at).toLocaleDateString('he-IL')}` : ''}
                        </div>
                        <div style={{ fontSize: 'var(--t-sm)', lineHeight: 1.6 }}>{pastCall.summary}</div>
                        {pastCall.agent_notes && (
                          <div style={{ marginTop: 6, fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
                            <strong>הערות נציג:</strong> {pastCall.agent_notes}
                          </div>
                        )}
                        {pastCall.transcript.length > 0 && (
                          <details style={{ marginTop: 6 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 'var(--t-xs)', color: 'var(--accent-ink)' }}>תמלול מלא</summary>
                            <div className="stack" style={{ gap: 3, marginTop: 6, maxHeight: 160, overflow: 'auto' }}>
                              {pastCall.transcript.map((l, i) => (
                                <div key={i} style={{ fontSize: 'var(--t-xs)' }}>
                                  <span style={{ color: 'var(--muted)' }}>{l.speaker === 'agent' ? 'נציג' : l.speaker === 'customer' ? 'לקוח' : ''}:</span> {l.txt}
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </>
                    ) : (
                      <Muted>אין סיכום זמין לשיחה זו.</Muted>
                    )}
                  </div>
                )}
              </Section>
            )}
          </div>
        </div>

        {loading && <div style={{ padding: '0 var(--pad-card) var(--pad-card)', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>טוען פרטי לקוח…</div>}
      </div>
  );

  // מוטמע: מוחזר כמו שהוא, בזרימת המסך.
  if (inline) return body;

  // צף: portal + scrim מעל שאר הממשק.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="כרטיס לקוח"
      style={{
        position: 'fixed', inset: 0, zIndex: 1300,
        background: 'oklch(0% 0 0 / 0.5)', display: 'grid', placeItems: 'center',
        padding: 'var(--gap)', animation: 'fadein 140ms ease',
      }}
    >
      {body}
    </div>,
    document.body
  );
}

/**
 * מסיים את הסיכום ומחזיר את הסופטפון ל-'ready'.
 *
 * חיוני: כשהכרטיס פתוח, WrapupOverlay עומד מנגד (ראו `!cardOpen` שם) והוא
 * הרכיב היחיד שקורא ל-finishWrapup. בלי הכפתור הזה הסופטפון נתקע ב-'wrap-up'
 * אחרי שיחה ואי אפשר לחייג שוב. הסיכום עצמו כבר נשמר אוטומטית ע"י הכרטיס.
 */
function FinishWrapupButton({ number }: { number: string }) {
  const finishWrapup = useSipStore((s) => s.finishWrapup);
  return (
    <button
      type="button"
      className="btn primary"
      onClick={() => {
        // "התפנה לשיחה הבאה" = לסגור הכל. finishWrapup לבדו רק מחזיר את
        // הסופטפון ל-'ready' ומשאיר את הכרטיס פתוח — ואז הוא מצטמצם מ-3
        // עמודות ל-2 (עמודת השיחה נעלמת) ונראה כמו כרטיס קטן שקפץ מחדש.
        // markSummarized מונע מ-WrapupOverlay לקפוץ במקומו על אותה שיחה.
        useCustomerCard.getState().markSummarized(number);
        useCustomerCard.getState().close();
        finishWrapup();
      }}
      style={{ width: '100%', marginTop: 8, gap: 6 }}
    >
      <Icon name="check" size={14} /> סיים סיכום והתפנה לשיחה הבאה
    </button>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-3)', padding: 'var(--pad-card)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ fontSize: 'var(--t-sm)' }}>{title}</strong>
        {hint && <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{hint}</span>}
      </div>
      <div className="stack" style={{ gap: 8 }}>{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="stack" style={{ gap: 3 }}>
      <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{label}</span>
      {children}
    </label>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>{children}</div>;
}
